'use client';
import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useIsomorphicEffect, useDidUpdate } from '@s7/hooks';
import { arrayOf } from '@s7/utils';
import { Refresh } from './domain.js';
import { useQueryInternalContext } from '../../context/QueryInternalContext.js';
import { useQueryContext } from '../../context/QueryContext.js';
import { hashQuery } from './utils/hashQuery.js';
import { serializeQuery } from './utils/serializeQuery.js';
import { isSingularIdent } from '../../cache/createCache/idents.js';
import { defaultResponseTransform } from '../utils/defaultResponseTransform.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useQuery = function(options) {
    var query = options.query, _options_transform = options.transform, transform = _options_transform === void 0 ? defaultResponseTransform : _options_transform, persist = options.persist, tmp = options.handlers, _ref = tmp === void 0 ? {} : tmp, onSuccess = _ref.onSuccess, onError = _ref.onError, onFinish = _ref.onFinish, tmp1 = options.refresh, invocationStrategy = tmp1 === void 0 ? Refresh.OnMountsAndQueryChanges : tmp1, _options_interactive = options.interactive, interactive = _options_interactive === void 0 ? true : _options_interactive, skip = options.skip, others = _object_without_properties_loose(options, [
        "query",
        "transform",
        "persist",
        "handlers",
        "refresh",
        "interactive",
        "skip"
    ]);
    var _useQueryInternalContext = useQueryInternalContext(), bus = _useQueryInternalContext[0];
    var _useQueryContext = useQueryContext(others), client = _useQueryContext[0], createCache = _useQueryContext[1];
    /// DERIVATIONS
    var queryId = hashQuery(serializeQuery(query));
    var cache = useMemo(function() {
        return createCache(queryId, persist);
    }, [
        queryId,
        persist
    ]);
    var withEffects = invocationStrategy === Refresh.OnMountsAndQueryChanges;
    var withMountEffect = !skip && (withEffects || invocationStrategy === Refresh.OnMount);
    var withUpdateEffect = !skip && (withEffects || invocationStrategy === Refresh.OnQueryChanges);
    /// STATE
    var channelRef = useRef(cache.channel);
    var isRepeatedQuery = useRef(false);
    var markQueryRepeated = function() {
        isRepeatedQuery.current = true;
    };
    var initialData = skip || cache.ssrInconsistent ? null : cache.restore();
    var _useState = useState(initialData), data = _useState[0], setData = _useState[1];
    /*
     * Как только инициализатор состояния начинает зависеть от другого инициализатора состояния,
     * проявляются ошибки гидрации, поэтому мы инициализируем состояние загрузки, как true,
     * предполагая процесс восстановления данных из кэша такой же загрузкой.
     * Далее состояние сбрасывается.
     *
     * Такая инициализация позволяет реализовать две взаимоисключающие стратегии гидрации,
     * подходящие для ssr-консистентных и ssr-неконсистентных провайдеров кэша.
     * */ var _useState1 = useState(withMountEffect), loading = _useState1[0], setLoading = _useState1[1];
    var _useState2 = useState(null), error = _useState2[0], setError = _useState2[1];
    /*
     * Восстановленные из кэша данные должны быть отображены сразу во избежание layout-shift.
     * К сожалению использование layout-эффекта - единственное возможное решение, так как
     * использование инициализаторов состояний приводит к ошибкам гидрации.
     * */ useIsomorphicEffect(function() {
        if (skip) {
            return;
        }
        if (data) {
            setLoading(false);
        }
        if (cache.ssrInconsistent) {
            var initialData = cache.restore();
            if (initialData) {
                setData(initialData);
                setLoading(false);
            } else if (withMountEffect) {
                setLoading(true);
            }
        }
    }, []);
    /// QUERY
    var forceHiddenUpdate = /*#__PURE__*/ _async_to_generator(function() {
        var _arrayOf, input, _$options, response, nextData, error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _arrayOf = arrayOf(query), input = _arrayOf[0], _$options = _arrayOf[1];
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        5,
                        6,
                        7
                    ]);
                    return [
                        4,
                        client(input, _$options)
                    ];
                case 2:
                    response = _state.sent();
                    setError(null);
                    return [
                        4,
                        transform(response.clone())
                    ];
                case 3:
                    nextData = _state.sent();
                    setData(nextData);
                    onSuccess == null ? void 0 : onSuccess(nextData);
                    return [
                        4,
                        cache.persist(nextData)
                    ];
                case 4:
                    channelRef.current = _state.sent();
                    markQueryRepeated();
                    return [
                        3,
                        7
                    ];
                case 5:
                    error = _state.sent();
                    setError(error);
                    onError == null ? void 0 : onError(error);
                    return [
                        3,
                        7
                    ];
                case 6:
                    onFinish == null ? void 0 : onFinish();
                    return [
                        7
                    ];
                case 7:
                    return [
                        2
                    ];
            }
        });
    });
    var forceInteractiveRefresh = /*#__PURE__*/ _async_to_generator(function() {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    setLoading(true);
                    setData(null);
                    return [
                        4,
                        forceHiddenUpdate()
                    ];
                case 1:
                    _state.sent();
                    setLoading(false);
                    return [
                        2
                    ];
            }
        });
    });
    var forceAdaptiveRefresh = /*#__PURE__*/ _async_to_generator(function() {
        var refresh;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (!isRepeatedQuery.current) return [
                        3,
                        2
                    ];
                    refresh = interactive ? forceInteractiveRefresh : forceHiddenUpdate;
                    return [
                        4,
                        refresh()
                    ];
                case 1:
                    _state.sent();
                    return [
                        3,
                        4
                    ];
                case 2:
                    return [
                        4,
                        forceInteractiveRefresh()
                    ];
                case 3:
                    _state.sent();
                    _state.label = 4;
                case 4:
                    return [
                        2
                    ];
            }
        });
    });
    var refresh = useCallback(/*#__PURE__*/ _async_to_generator(function(force) {
        var restoredCache;
        return _ts_generator(this, function(_state) {
            if (force) {
                forceAdaptiveRefresh();
                return [
                    2
                ];
            }
            restoredCache = cache.restore();
            /*
             * Если смогли восстановить из кэша - происходит ранняя остановка.
             * Запрос маркируется грязным для применения стратегии адаптивного обновления.
             * */ if (restoredCache) {
                setData(restoredCache);
                markQueryRepeated();
                return [
                    2
                ];
            }
            forceAdaptiveRefresh();
            return [
                2
            ];
        });
    }), [
        queryId
    ]);
    var reset = useCallback(function() {
        setLoading(false);
        setError(null);
        setData(null);
    }, []);
    /// FX
    useEffect(function() {
        if (withMountEffect) {
            refresh();
        }
    }, []);
    useDidUpdate(function() {
        if (withUpdateEffect) {
            refresh();
        }
    }, [
        queryId
    ]);
    useEffect(function() {
        var channel = channelRef.current;
        if (channel) {
            if (isSingularIdent(channel)) {
                return bus.on(channel, function() {
                    forceAdaptiveRefresh();
                });
            }
            return bus.on(channel.key, function(ident) {
                if (isSingularIdent(ident)) {
                    if (arrayOf(channel.ident).includes(ident)) {
                        forceAdaptiveRefresh();
                    }
                } else {
                    forceAdaptiveRefresh();
                }
            });
        }
    }, []);
    return [
        data,
        {
            loading: loading,
            error: error,
            refresh: refresh,
            reset: reset
        }
    ];
};

export { useQuery };
